import Tooltip from '@material-ui/core/Tooltip';
import moment from 'moment';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import AssignMemberToOrderStage from '@components/assign-team-member/AssignMemberToOrderStage';
import DateRangeComponent from '@components/DateRangeComponent';
import LoaderComponent from '@components/LoaderComponent';
import SampleReply from '@components/SampleReply';
import { toastSuccess } from '@components/Toast';

import useClickOutside from '@hooks/useClickOutside';
import useFetch from '@hooks/useFetch';

import NewPostSection from '@features/task/components/OrderManageComponents/NewPostSection';
import ExFactorySupplierQuote from '@features/order-dashboard/component/ExFactorySupplierQuote';

import { setCurrentActivePage } from '@store/actions/ActivePageAction';
import { clearAllChats } from '@store/actions/ChatAction';

import tnaService from '@services/tna-service';

import {
	changeDateFormat,
	getShortName,
	getUserInfo,
	packTag,
	setTag,
	STATIC_SET_CONSTANTS,
	validateNumber
} from '@utils/Util';

import { OrderContext } from '@context/OrderContext';

const OrderTask = ({ callback, requestFrom = '', rfqId }) => {
	const dispatch = useDispatch();
	const { deliverableId, orderId, closeModal, reviseInfo, brandName, resetRevisionInfo } =
		useContext(OrderContext);
	const { responseData: memberData } = useFetch('getProjectMembers', orderId);
	const [deliverableDetails, setDeliverableDetails] = useState(null);
	const [orderMemberList, setOrderMemberList] = useState([]);
	const [isShowInputTarget, setIsShowInputTarget] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [hasNext, setHasNext] = useState(true);
	const [inputPoQuantity, setInputPoQuantity] = useState(null);
	const [updatePoQuantity, setUpdatePoQuantity] = useState(null);
	const [editSubTaskId, setEditSubTaskId] = useState(null);
	const [editSubTaskDateId, setEditSubTaskDateId] = useState(null);
	const [isAllCommentsOn, setIsAllCommentsOn] = useState(false);
	const [newlyAddedSubTaskList, setNewlyAddedSubtaskList] = useState([]);
	const [userInfo, setUserInfo] = useState(null);
	const [highlightId, setHighlightId] = useState(null);
	const [page, setPage] = useState(0);
	const [commentsData, setCommentsData] = useState([]);
	const [reviseDetails, setReviseDetails] = useState(reviseInfo);
	const [isReview, setIsReview] = useState(null);
	const generalTaskList = deliverableDetails?.taskList;
	const materialTaskList = deliverableDetails?.materialTaskList;
	const stepProduct = deliverableDetails?.stepProduct;
	const STEP_TYPE = deliverableDetails?.stageResponse?.stageConstants;
	const isNewPost = useSelector((store) => store.chatReducer.newMessage);
	const POREF = useRef(null);
	const location = useLocation();

	const onOutSideClick = () => {
		if (isShowInputTarget) {
			setIsShowInputTarget(false);
		}
	};
	useClickOutside(POREF, onOutSideClick);

	const scrollDownInChatBody = () => {
		let container = document.getElementById('taskPostScroll');
		let o1 = container?.scrollHeight - container?.clientHeight;
		let elem = document.getElementById('taskPostScroll');
		elem.scrollTop = o1 || 0;
	};

	const onSetDeliverableDetails = (deliverable) => {
		setDeliverableDetails(deliverable);
	};

	const onGetDeliverableDetails = async () => {
		setIsLoading(true);
		const data = await tnaService.onGetDeliverableDetails(deliverableId);
		setDeliverableDetails(data);
		setIsLoading(false);
	};

	useEffect(() => {
		onGetDeliverableDetails();
	}, [deliverableId]);

	useEffect(() => {
		if (deliverableDetails?.id) {
			onGetCommentsData(0);
		}
	}, [deliverableDetails?.id, isAllCommentsOn]);

	useEffect(() => {
		const params = new URLSearchParams(location.search);
		const highlightPostId = params.get('postId');
		if (parseInt(highlightPostId) !== undefined) {
			setHighlightId(highlightPostId);
		}
		dispatch(
			setCurrentActivePage({
				id: deliverableId,
				type: 'TASK_CONVERSATION',
				pageType: 'DETAILS'
			})
		);
		setUserInfo(getUserInfo());
		return () => {
			dispatch(
				setCurrentActivePage({
					id: null,
					type: requestFrom === 'ORDER_DASHBOARD' ? 'ORDER_DASHBOARD_UNREAD_POST_DELIVERABLE' : '',
					pageType: 'DETAILS'
				})
			);
		};
	}, []);

	useEffect(() => {
		onSetDeliverableDetails(deliverableDetails);
		setOrderMemberList(memberData?.memberList);
	}, [deliverableDetails, memberData]);

	useEffect(() => {
		scrollDownInChatBody();
		const postResponse = isNewPost;
		if (postResponse?.text && getUserInfo()?.id !== postResponse?.postedBy.id) {
			const oldPosts = [...commentsData];
			if (postResponse?.text && getUserInfo()?.id !== postResponse?.postedBy.id) {
				if (postResponse?.parentPostId) {
					const index = oldPosts.findIndex((post) => post.id === postResponse?.parentPostId);
					oldPosts[index].commentList.push(postResponse);
				} else {
					oldPosts.push(postResponse);
				}
				setCommentsData([...oldPosts]);
				scrollDownInChatBody();
				toastSuccess('New message received');
				setTimeout(() => {
					dispatch(clearAllChats());
				}, 1000);
			}
		}
	}, [isNewPost?.id]);

	const onGetCommentsData = async (pageNumber = 0) => {
		setIsLoading(true);
		const paramsData = {
			orderId: deliverableDetails?.orderId,
			page: pageNumber,
			ProductId: deliverableDetails?.stepProduct?.id
		};
		if (!isAllCommentsOn) {
			paramsData.deliverableId = deliverableId;
		}
		const allComments = (await tnaService.fetchPostComments(paramsData)) || [];
		if (allComments !== undefined) {
			allComments.reverse();
		}
		setHasNext(allComments?.length === 10);
		if (pageNumber === 0) {
			setCommentsData(allComments);
			scrollDownInChatBody();
		} else {
			setCommentsData([...allComments, ...commentsData]);
			setPage((page) => page + 1);
			let elem = document.getElementById('taskPostScroll');
			elem.scrollTop = 355;
		}
		setIsLoading(false);
	};

	const onScrollComments = () => {
		const wrappedElement = document.getElementById('taskPostScroll');
		if (wrappedElement.scrollTop === 0) {
			if (hasNext) {
				onGetCommentsData(page + 1);
			}
		}
	};

	const onUpdateQuantity = async (id) => {
		setIsLoading(true);
		const data = await tnaService.onSubmitPoQuantity(id, inputPoQuantity);
		if (data?.success) {
			toastSuccess(data?.message);
			setInputPoQuantity(null);
			setIsShowInputTarget(false);
			setDeliverableDetails(data?.payload);
			callback();
		}
		setIsLoading(false);
	};

	const onChangeDateRange = async (value1, _value2, id) => {
		const body = {
			endDate: value1
		};
		const endDateUpdateResponse = await tnaService.taskEndDateUpdate(body, id);
		if (endDateUpdateResponse?.success) {
			toastSuccess(endDateUpdateResponse?.message);
			onGetDeliverableDetails();
			callback();
		}
	};

	const handleSubtaskChange = (i, e) => {
		let newSubTask = [...newlyAddedSubTaskList];
		newSubTask[i][e.target.name] = e.target.value;
		setNewlyAddedSubtaskList(newSubTask);
	};

	const onAddSubTask = () => {
		setNewlyAddedSubtaskList([
			...newlyAddedSubTaskList,
			{ subTask: '', date: deliverableDetails?.maxTaskEndDate, isActive: false }
		]);
	};

	const handleProductionQty = (e) => {
		setUpdatePoQuantity(e.target.value);
	};

	const handleSubmitProductionQty = async () => {
		if (!updatePoQuantity) return;
		setIsLoading(true);
		const updateQtyResponse = await tnaService.qtyUpdate(
			deliverableDetails?.taskList[0]?.id,
			updatePoQuantity
		);
		if (updateQtyResponse?.success) {
			toastSuccess(updateQtyResponse?.message);
			setUpdatePoQuantity(null);
			onGetDeliverableDetails();
			callback();
			onAddNewPost(updateQtyResponse?.payload);
		}
		setIsLoading(false);
	};

	const onRemoveTask = async (id) => {
		setIsLoading(true);
		const data = await tnaService.onDleteTask(id);
		if (data?.success) {
			toastSuccess(data?.message);
			let updatedSubTask = deliverableDetails?.subTaskList?.filter((item) => item.id !== id);
			setDeliverableDetails({
				...deliverableDetails,
				subTaskList: updatedSubTask
			});
		}
		setIsLoading(false);
	};

	const onDeleteProductionQuantity = async (stepId, quantityId) => {
		setIsLoading(true);
		const data = await tnaService.onDeletePoQuantity(stepId, quantityId);
		callback();
		setDeliverableDetails(data);
		setIsLoading(false);
	};

	const completeSubTask = async (subTask) => {
		setIsLoading(true);
		const body = {
			id: subTask.id,
			name: subTask.name,
			endDate: subTask.endDate,
			status: 'COMPLETED'
		};
		const subTaskCreateresponse = await tnaService.subtaskUpdate(subTask.id, body);
		if (subTaskCreateresponse?.success) {
			toastSuccess(subTaskCreateresponse?.message);
			setDeliverableDetails({
				...deliverableDetails,
				subTaskList: subTaskCreateresponse.payload.subTaskList
			});
		}
		setIsLoading(false);
	};

	const editSubTask = async (e, subTask) => {
		setIsLoading(true);
		setEditSubTaskId(null);
		setEditSubTaskDateId(null);
		let body = { ...subTask };
		body[e.target.name] = e.target.value;
		const subTaskCreateresponse = await tnaService.subtaskUpdate(subTask.id, body);
		if (subTaskCreateresponse?.success) {
			toastSuccess(subTaskCreateresponse?.message);
			setDeliverableDetails({
				...deliverableDetails,
				subTaskList: subTaskCreateresponse.payload.subTaskList
			});
		}
		setIsLoading(false);
	};

	const removeFormFields = (i) => {
		let subTaskformValues = [...newlyAddedSubTaskList];
		subTaskformValues.splice(i, 1);
		setNewlyAddedSubtaskList([...subTaskformValues]);
	};

	const postSubtask = async ({ date, subTask }) => {
		setIsLoading(true);
		const body = {
			endDate: date,
			id: deliverableId,
			name: subTask,
			status: 'PENDING'
		};
		const subTaskCreateresponse = await tnaService.subtaskCreate(deliverableId, body);
		if (subTaskCreateresponse?.success) {
			toastSuccess(subTaskCreateresponse?.message);
			setDeliverableDetails({
				...deliverableDetails,
				subTaskList: subTaskCreateresponse.payload.subTaskList
			});
		}
		setIsLoading(false);
	};

	const handleSubtaskComplete = (i) => {
		let subTaskformValues = [...newlyAddedSubTaskList];
		subTaskformValues[i].isActive = true;
		setNewlyAddedSubtaskList([...subTaskformValues]);
		postSubtask(subTaskformValues[i]);
	};

	const onApproveTask = async (taskId) => {
		setIsLoading(true);
		const response = await tnaService.onApproveTask(taskId);
		if (response?.success) {
			setDeliverableDetails(response?.payload?.deliverableDetails);
			onGetCommentsData(0);
			callback();
		}
		setIsLoading(false);
	};

	const onAddNewPost = (newPost) => {
		let prevData = [...commentsData];
		prevData.push(newPost);
		setCommentsData([...prevData]);
	};

	// all renders
	const renderTopHeader = () => (
		<div className="haeder-task-title d-flex align-items-center">
			<div className="header-photo mr-1">
				{stepProduct?.setPackStyleFlag === STATIC_SET_CONSTANTS.SET_PRODUCT ? (
					<div className="set-image-layout" style={{ width: '56px' }}>
						<img
							src={
								stepProduct?.setImageList[0] ||
								`${process.env.PUBLIC_URL}/images/default_product.svg`
							}
							alt="product"
						/>
						<img
							src={
								stepProduct?.setImageList[1] ||
								`${process.env.PUBLIC_URL}/images/default_product.svg`
							}
							alt="product"
						/>
					</div>
				) : (
					<img
						src={stepProduct?.image || `${process.env.PUBLIC_URL}/images/default_product.svg`}
						alt="product"
					/>
				)}
			</div>
			{stepProduct && (
				<div className="header-info ml-2">
					{stepProduct?.setPackStyleFlag === STATIC_SET_CONSTANTS.SET_PRODUCT && setTag()}
					{stepProduct?.setPackStyleFlag === STATIC_SET_CONSTANTS.PACK_PRODUCT && packTag()}
					<ul className="common-list-component">
						<li>
							<span className="semibold-12">{stepProduct?.referenceNumber}</span>
						</li>
						<li>{stepProduct?.market}</li>
						<li>{stepProduct?.subCategory}</li>
					</ul>
					<p className="regular-12 gray_dark_02 mt-1 mb-1">{deliverableDetails?.poNumber}</p>
				</div>
			)}
		</div>
	);

	const renderTargetForm = (task) => {
		return (
			<div className="po-qty-popup shadow-8dp" ref={POREF}>
				<p className="regular-12 gray_dark_02 mb-2">
					<span className="weight-500">PO QTY:</span>{' '}
					<span className="gray_dark_01 mb-1">{task?.poQuantity}</span>
				</p>
				<p className="regular-14 gray_dark_02 mb-1">Cutting target qty</p>
				<input
					type="number"
					min={1}
					name="quantity"
					placeholder={'00'}
					value={inputPoQuantity || ''}
					onChange={(e) => setInputPoQuantity(e.target.value)}
					onKeyPress={validateNumber}
					autoFocus
				/>
				<div className="text-right mt-3">
					<button
						className="button text-only gray_dark_02 mr-2"
						onClick={() => setIsShowInputTarget(false)}
					>
						CANCEL
					</button>
					<button
						disabled={!inputPoQuantity}
						className="button text-only gray_dark_01 weight-500"
						onClick={() => onUpdateQuantity(task.id)}
					>
						UPDATE
					</button>
				</div>
			</div>
		);
	};

	const renderStatusIcon = (task) => {
		if (
			task?.formattedTaskStatus === 'EXPIRED' ||
			task?.formattedTaskStatus === 'PENDING' ||
			task?.formattedTaskStatus === 'RUNNING'
		)
			return (
				<>
					<Tooltip title="Complete" placement="top-start" arrow>
						<img
							src={`${process.env.PUBLIC_URL}/icons/due-green.svg`}
							alt="due"
							className="state"
							onClick={() => {
								if (task?.stepName === 'Handover to forwarder') {
									setIsReview(task.id);
								} else onApproveTask(task.id);
							}}
						/>
					</Tooltip>
					{task?.stepType === 'REVIEW' ? (
						<Tooltip title="Revise" placement="top-start" arrow>
							<img
								onClick={() =>
									setReviseDetails({
										id: task.id,
										stepName: task.stepName
									})
								}
								className="state"
								src={`${process.env.PUBLIC_URL}/icons/Update-red.svg`}
								alt="update"
							/>
						</Tooltip>
					) : null}
				</>
			);
		else
			return (
				<img
					src={`${process.env.PUBLIC_URL}/icons/Success.svg`}
					alt="success"
					className="state"
					onClick={() => {
						if (task?.stepName === 'Handover to forwarder') {
							setIsReview(task.id);
						}
					}}
				/>
			);
	};

	const renderForm = (task) => {
		return (
			['Cutting', 'Winding'].includes(deliverableDetails?.name) &&
			!['COMPLETED', 'APPROVED'].includes(task?.status) && (
				<div className="production-details">
					<button className="production-details-btn">
						{!task?.productionTarget && <span className="input-qty">Input target qty</span>}
						<img
							className="cursor-pointer ml-1"
							src={`${process.env.PUBLIC_URL}/icons/down-arrow-gray-dark-02.svg`}
							alt="down arrow"
							onClick={() => setIsShowInputTarget(!isShowInputTarget)}
						/>
					</button>
					{isShowInputTarget && renderTargetForm(task)}
				</div>
			)
		);
	};

	const renderInputTargetItem = (task) => {
		let title = Math.sign(task?.extraProductionTargetPercentage) === -1 ? 'PO' : 'PO+';
		if (task?.productionTarget) {
			return (
				<div className="production-details ml-3">
					<div className="production-details-btn d-flex align-items-center">
						<span className="blue_dark_01">{task?.actualProductionQuantity || 0}</span>/
						{task?.productionTarget}
						<span className="percentage pl-1">
							({title}
							{parseFloat(task?.extraProductionTargetPercentage).toFixed(2)}%)
						</span>
						<span className="devider">|</span> Bal:
						<span className="gray_dark_01">
							{parseInt(task?.productionTarget) - parseInt(task?.actualProductionQuantity || 0)}
						</span>
						{renderForm(task)}
					</div>
				</div>
			);
		} else {
			return renderForm(task);
		}
	};

	const renderStepName = (name) => (
		<Tooltip title={name} placement="top-start" arrow>
			<span className="cursor-pointer"> {getShortName(name, 27)}</span>
		</Tooltip>
	);

	const renderTaskRow = (task) => {
		return (
			<div key={task?.id} className="single-task">
				<div className="d-flex justify-content-between align-items-center pb-2">
					<p>
						{renderStatusIcon(task)}
						{generalTaskList && generalTaskList?.length === 1
							? renderStepName(task?.stepDescription)
							: renderStepName(task?.stepName)}

						{(task?.formattedTaskStatus === 'COMPLETED' ||
							task?.formattedTaskStatus === 'APPROVED' ||
							task?.formattedTaskStatus === 'LATE_APPROVED') && (
							<>
								<span className="green state ml-2 mr-1">
									<img
										src={`${process.env.PUBLIC_URL}/icons/tick-green.png`}
										className="mr-1"
									/>
									{task?.actualEndDate && moment(task?.actualEndDate).format('D MMM')}
								</span>
								{task?.dateOver > 0 ? (
									<span className="red date-count state mr-1">+{task?.dateOver}d</span>
								) : task?.dateOver < 0 ? (
									<span className="green date-count state mr-1">
										+{Math.abs(task?.dateOver)}d
									</span>
								) : null}
							</>
						)}

						{task?.formattedTaskStatus === 'EXPIRED' && (
							<>
								<span className="status unsafe state ml-2 mr-1">
									<img
										src={`${process.env.PUBLIC_URL}/icons/alert-octagon-white.svg`}
										alt="update"
									/>{' '}
									+{task?.dateOver}d
								</span>
							</>
						)}

						{task?.revisionCount && (
							<span className="status red-alert ml-1 mr-1">
								<img src={`${process.env.PUBLIC_URL}/icons/Update.svg`} alt="update" />
								{task?.revisionCount}
							</span>
						)}
					</p>

					<AssignMemberToOrderStage
						memberList={orderMemberList}
						taskInfo={task}
						onAddOrDeleteSuccess={() => {
							onGetDeliverableDetails();
							callback();
						}}
					/>
					<span>
						<DateRangeComponent
							item={{ value1: task?.startDate, value2: task?.endDate }}
							onChange={onChangeDateRange}
							itemId={task?.id}
							single
						/>
					</span>
				</div>

				{STEP_TYPE === 'PRODUCTION' ? renderInputTargetItem(task) : null}
				{STEP_TYPE === 'PRODUCTION' &&
					task?.productionTarget &&
					renderQuantityResponseList(task?.status)}

				{isReview === task.id && (
					<ExFactorySupplierQuote
						rfqId={rfqId}
						onCloseModal={() => setIsReview(null)}
						onSuccess={() => {
							onApproveTask(task.id);
							setIsReview(null);
						}}
						supplierDetails={{
							supplierName: deliverableDetails?.supplierName,
							supplierId: deliverableDetails?.supplierId
						}}
					/>
				)}
			</div>
		);
	};

	const renderAddSubTask = () => (
		<>
			{!deliverableDetails?.isCompleted && (
				<div className="add-subtask my-2">
					<Tooltip title="Add new sub-task" placement="top-start" arrow>
						<img
							src={`${process.env.PUBLIC_URL}/icons/add-round.svg`}
							alt="add"
							className="cursor-pointer"
							onClick={onAddSubTask}
						/>
					</Tooltip>
				</div>
			)}
			{renderSubTaskList()}
		</>
	);

	const renderGeneeralTaskList = () => (
		<>
			{generalTaskList?.length > 1 ? <p className="semibold-12">{deliverableDetails?.name}</p> : null}
			{generalTaskList.map((item) => renderTaskRow(item))}
			{renderAddSubTask()}
		</>
	);

	const renderMaterialTaskList = () => (
		<>
			{materialTaskList?.map((item) => {
				return (
					<>
						<p className="semibold-12 task-title">{item?.stepMaterial?.materialName}</p>
						{item?.stepResponseList?.map((step) => renderTaskRow(step))}
					</>
				);
			})}
			{renderAddSubTask()}
		</>
	);

	const renderQuantityResponseList = (status) => {
		return (
			<>
				<div className="update-today-qty mt-3">
					<input
						type="number"
						placeholder="Update today’s qty"
						value={updatePoQuantity || ''}
						onChange={(e) => handleProductionQty(e)}
						min={1}
						onKeyPress={(event) => {
							if (event.key === 'Enter') handleSubmitProductionQty();
							else {
								validateNumber(event);
							}
						}}
						disabled={['COMPLETED', 'APPROVED'].includes(status)}
					/>
				</div>

				<div className="added-qty-chips">
					{deliverableDetails?.taskList[0]?.quantityResponseList?.map((item) => (
						<div className="qty-chip" key={item.id}>
							<span className="gray_dark_01">{item?.quantity}</span>
							<span className="devider">|</span>
							<span className="gray_dark_02">
								{changeDateFormat(item?.productionDate, 'YYYY-MM-DD', 'DD MMM')}
							</span>
							{item.productionDate === moment().format('YYYY-MM-DD') &&
								!['COMPLETED', 'APPROVED'].includes(status) && (
									<img
										src={`${process.env.PUBLIC_URL}/icons/chip-close.svg`}
										alt="close"
										onClick={() =>
											onDeleteProductionQuantity(
												deliverableDetails?.taskList[0].id,
												item.id
											)
										}
									/>
								)}
						</div>
					))}
				</div>
			</>
		);
	};

	const renderSubTaskList = () => (
		<div className="added-task-list scroll-y-label">
			{deliverableDetails?.subTaskList?.map((subTask) => {
				return (
					<div key={subTask?.id} className="added-single-task">
						{subTask.status === 'PENDING' && (
							<img
								src={`${process.env.PUBLIC_URL}/icons/delete_gray.svg`}
								alt="delete"
								className="delete-cm"
								onClick={() => onRemoveTask(subTask.id)}
							/>
						)}
						<div className="task-name d-flex align-items-center w-75">
							{subTask.status === 'PENDING' && (
								<img
									src={`${process.env.PUBLIC_URL}/icons/due-green.svg`}
									onClick={() => completeSubTask(subTask)}
								/>
							)}

							{subTask.status !== 'PENDING' && (
								<img src={`${process.env.PUBLIC_URL}/icons/Success.svg`} />
							)}
							{(!editSubTaskId || editSubTaskId !== subTask?.id) && (
								<span
									className="semibold-12 d-block w-100"
									onClick={() => {
										if (subTask.status !== 'COMPLETED') {
											setEditSubTaskId(subTask?.id);
										}
									}}
								>
									{subTask?.name}
								</span>
							)}
							{editSubTaskId && editSubTaskId === subTask?.id && (
								<input
									type="text"
									autoComplete="off"
									name="name"
									defaultValue={subTask.name}
									placeholder="Write task name"
									autoFocus
									onKeyPress={(e) => {
										if (e.key === 'Enter' && e.target.value.trim()) {
											editSubTask(e, subTask);
										}
									}}
								/>
							)}
						</div>
						{(!editSubTaskDateId || editSubTaskDateId !== subTask.id) && (
							<div
								className="date w-25 text-right"
								onClick={
									subTask.status !== 'COMPLETED'
										? () => setEditSubTaskDateId(subTask.id)
										: null
								}
							>
								{moment(subTask.endDate).format('D MMM, YY')}
							</div>
						)}
						{editSubTaskDateId && editSubTaskDateId === subTask.id && (
							<div className="date">
								<input
									disabled={subTask.status === 'COMPLETED'}
									type="date"
									name="endDate"
									defaultValue={subTask.endDate}
									onBlur={(event) => editSubTask(event, subTask)}
								/>
							</div>
						)}
					</div>
				);
			})}

			{newlyAddedSubTaskList?.map((subtask, index) => {
				return !subtask.isActive ? (
					<div key={index} className="added-single-task new">
						<img
							src={`${process.env.PUBLIC_URL}/icons/close.svg`}
							alt="delete"
							className="close-subtask cursor-pointer"
							onClick={() => removeFormFields(index)}
						/>
						<div className="task-name d-flex align-items-center">
							{!subtask.isActive && (
								<img src={`${process.env.PUBLIC_URL}/icons/due-green.svg`} />
							)}
							{subtask.isActive && (
								<img src={`${process.env.PUBLIC_URL}/icons/delivered-round.svg`} />
							)}
							<input
								type="text"
								autoComplete="off"
								name="subTask"
								value={subtask.subTask}
								autoFocus
								placeholder="Write task name"
								onChange={(e) => handleSubtaskChange(index, e)}
								onKeyPress={(event) => {
									if (event.key === 'Enter' && subtask.subTask.trim())
										handleSubtaskComplete(index);
								}}
							/>
						</div>
						<div className="date">
							<input
								type="date"
								name="date"
								min={deliverableDetails?.minTaskStartDate}
								max={deliverableDetails?.maxTaskEndDate}
								value={subtask.date}
								onChange={(e) => handleSubtaskChange(index, e)}
							/>
						</div>
					</div>
				) : null;
			})}
		</div>
	);

	const renderAllCommentsToggleButton = () => (
		<div className="d-flex justify-content-between px-3 py-2">
			<p className="regular-14 weight-500 gray_dark_01">Activities</p>
			<p>
				<span className="cursor-pointer mr-1">
					<img
						src={
							isAllCommentsOn
								? `${process.env.PUBLIC_URL}/icons/toggle-on.svg`
								: `${process.env.PUBLIC_URL}/icons/toggle-off.svg`
						}
						alt="toggle"
						onClick={() => {
							setIsAllCommentsOn((prev) => !prev);
						}}
					/>
				</span>
				All comments
			</p>
		</div>
	);
	return (
		<LoaderComponent loading={isLoading}>
			<div role="document">
				<div>
					<div className="modal-header">
						{renderTopHeader()}
						{generalTaskList ? (
							<div>
								<div className="general-tasks">
									<div className="task-group">{renderGeneeralTaskList()}</div>
								</div>
								{renderAllCommentsToggleButton()}
							</div>
						) : null}

						{/* Modal CLose button*/}
						<button
							type="button"
							className="close"
							data-dismiss="modal"
							aria-label="Close"
							onClick={closeModal}
						>
							<span aria-hidden="true">
								<svg
									width={14}
									height={14}
									viewBox="0 0 14 14"
									fill="none"
									xmlns="http://www.w3.org/2000/svg"
								>
									<path
										d="M14 1.41L12.59 0L7 5.59L1.41 0L0 1.41L5.59 7L0 12.59L1.41 14L7 8.41L12.59 14L14 12.59L8.41 7L14 1.41Z"
										fill="#222222"
										fillOpacity="0.87"
									/>
								</svg>
							</span>
						</button>
					</div>
					<div
						className={`modal-body custom-scrollbar ${
							materialTaskList ? 'material-tasks' : 'other-tasks'
						}`}
						id="taskPostScroll"
						onScroll={() => onScrollComments()}
					>
						{materialTaskList ? (
							<div>
								<div className="general-tasks">
									<div className="task-group">{renderMaterialTaskList()}</div>
								</div>
								{renderAllCommentsToggleButton()}
							</div>
						) : null}

						<div className="conversation-container">
							{commentsData?.map((post) => {
								return (
									<div
										className={`${
											parseInt(highlightId) === post.id ? 'highlight-wrapper' : ''
										}`}
										key={post.id}
									>
										<SampleReply
											postData={post}
											key={post.id}
											orderMemberList={orderMemberList}
											isAllCommentsOn={isAllCommentsOn}
											userInfo={userInfo}
											commentSubmitUrl={'commentOnTask'}
											commentSubmitparam={'?fromTimeline=false'}
											commentFrom="ORDER"
										/>
									</div>
								);
							})}
							{commentsData?.length === 0 && !isLoading && (
								<div className="no-comments">No comment found</div>
							)}
						</div>
					</div>
					<NewPostSection
						onSetDeliverableDetails={onSetDeliverableDetails}
						onAddNewPost={onAddNewPost}
						orderMemberList={orderMemberList}
						userInfo={userInfo}
						deliverableId={deliverableId}
						orderId={orderId}
						closeModal={closeModal}
						reviseInfo={reviseDetails}
						brandName={brandName}
						onGetCommentsData={onGetCommentsData}
						onResetRevise={() => {
							setReviseDetails(null);
							callback();
						}}
						resetRevisionInfo={resetRevisionInfo}
					/>
				</div>
			</div>
		</LoaderComponent>
	);
};

export default OrderTask;
